// src/components/FaceCluster.js
import React from 'react';
import { Droppable } from '@hello-pangea/dnd';
import { Card, CardContent, CardHeader, Box, TextField, Typography, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import InteractiveFace from './InteractiveFace';

const FaceCluster = ({ cluster, onRemoveFace, onBirthYearChange, onNameChange, onRemoveCluster }) => {
  const theme = useTheme();
  const isUnassigned = cluster.id === 'cluster_unassigned';

  const handleIntegerChange = (event) => {
    const value = event.target.value;
    if (value === '' || /^[0-9]+$/.test(value)) {
      onBirthYearChange(cluster.id, value);
    }
  };

  const handleNameChange = (event) => {
    if (onNameChange) {
      onNameChange(cluster.id, event.target.value);
    }
  };

  const cardStyle = {
    m: 1,
    minWidth: 280,
    flexShrink: 0,
    backgroundColor: isUnassigned ? theme.palette.action.selected : 'background.paper',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  };

  const droppableStyle = (snapshot) => ({
    display: 'flex',
    flexWrap: 'wrap',
    minHeight: '100px',
    p: 0.5,
    backgroundColor: snapshot.isDraggingOver ? theme.palette.primary.light : 'transparent',
    borderRadius: 1,
    transition: 'background-color 0.2s ease',
    flexGrow: 1,
  });

  return (
    <Card sx={cardStyle} elevation={3}>
      <CardHeader
        title={
          !isUnassigned && onNameChange ? (
            <TextField
              variant="standard"
              value={cluster.name}
              onChange={handleNameChange}
              fullWidth
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                '.MuiInput-underline:before': { borderBottomStyle: 'dashed' },
                '.MuiInput-root': { marginTop: '0px' },
                '&:hover .MuiInput-underline:before': { borderBottomStyle: 'solid' },
              }}
            />
          ) : (
            <Typography variant="h6" component="div">
              {cluster.name || `Identity ${cluster.id}`}
            </Typography>
          )
        }
        action={
          !isUnassigned && (
            <IconButton onClick={() => onRemoveCluster(cluster.id)} aria-label="remove cluster">
              <DeleteIcon />
            </IconButton>
          )
        }
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      />
      <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {!isUnassigned && (
          <TextField
            label="Birth Year"
            type="text"
            inputMode="numeric"
            variant="outlined"
            size="small"
            // --- THIS IS THE FIX: Use snake_case to match the API response ---
            value={cluster.birth_year || ''}
            onChange={handleIntegerChange}
            sx={{ width: '100%', mb: 2 }}
            onMouseDown={(e) => e.stopPropagation()}
          />
        )}
        <Droppable droppableId={String(cluster.id)} direction="horizontal">
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={droppableStyle(snapshot)}
            >
              {cluster.faces.map((face, index) => {
                const uniqueFaceId = face.face_id || face.id;
                return (
                  <InteractiveFace
                    key={uniqueFaceId}
                    face={{ ...face, id: uniqueFaceId }}
                    index={index}
                    onRemoveFace={() => onRemoveFace(uniqueFaceId)}
                    size="128px"
                  />
                );
              })}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </CardContent>
    </Card>
  );
};

export default FaceCluster;